import { Component, effect, input, output, viewChild } from '@angular/core';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';
import { FtBoolFieldComponent } from './ft-bool-field.component';
import { FtTextFieldComponent } from './ft-text-field.component';
import { FtNumberFieldComponent } from './ft-number-field.component';
import { FtTextareaFieldComponent } from './ft-textarea-field.component';
import { FtHtmlFieldComponent } from './ft-html-field.component';
import { FtCodeFieldComponent } from './ft-code-field.component';

/** A field-editor modal request. `null` = closed. */
export interface FieldEditorModalConfig {
    /** Field-editor type. Phase 2 grows this (select / date next). 'html' = textAngular; 'css'/'js' = CodeMirror. */
    type: 'bool' | 'text' | 'number' | 'textarea' | 'html' | 'css' | 'js';
    /**
     * The object whose fields the editor mutates (e.g. `data.contest`), OR a getter returning it.
     * A getter reproduces v1's LIVE scope binding (`datasource="vm.x.y"` re-evaluated every digest):
     * hosts whose model root is REPLACED while the modal is open (e.g. the cert-email editors,
     * where getEmailsData can resolve after a fast field-open) must pass a getter — a captured
     * reference would leave the modal rendering the stale pre-load object forever.
     */
    datasource: any;
    /** A field name, `{name,label}`, or a list of either. */
    fields: any;
    /** Per-field options for the text editor (e.g. `{ 'ng-maxlength': 50 }`). */
    fieldsOptions?: any;
    title?: string;
    description?: string;
    /** ftModal size class (xs/sm/md/lg/xl/full). Defaults to sm. */
    size?: string;
    doneText?: string;
    /**
     * v1 ftModal `on-done` handler (e.g. the contest `save()`). May return a promise —
     * the modal stays open until it resolves, then closes (matches modal.js done()).
     */
    onDone?: () => any;
    /**
     * Cancel handler. When set, it OWNS rollback (the diff-save host restores its open-time
     * snapshot, e.g. via native-diff `revertDiffs`) and is called INSTEAD of the shell's manual
     * per-field snapshot rollback. When NOT set (the bool path), the shell rolls back itself.
     */
    onCancel?: () => void;
    /** Close cleanup, called after BOTH done and cancel (e.g. to release the diff-save snapshot). */
    onClose?: () => void;
}

/**
 * Native field-editor modal shell: FtModalComponent (a CDK overlay) hosting
 * FtModalChromeComponent which projects the field component for the config's type.
 * Host-driven: sets `[config]` to open, nulls it on `(closed)`. Done calls
 * `config.onDone()` (awaits a promise) then closes; Cancel/X rolls back the
 * snapshotted field values then closes.
 *
 * Its own CDK overlay (not a bridged ftModal) is required: the contest sheet renders
 * inside a CDK overlay (z-index 999999), so a body-appended ftModal (z-index 3000)
 * would open behind it and be invisible.
 */
@Component({
    selector: 'ft-field-editor-modal',
    imports: [FtModalComponent, FtModalChromeComponent, FtBoolFieldComponent, FtTextFieldComponent, FtNumberFieldComponent, FtTextareaFieldComponent, FtHtmlFieldComponent, FtCodeFieldComponent],
    templateUrl: './field-editor-modal.component.html',
})
export class FtFieldEditorModalComponent {
    readonly config = input<FieldEditorModalConfig | null>(null);
    /** Emitted after the modal closes (done or cancel). The host should null `config`. */
    readonly closed = output<void>();

    /** The mounted code field (css/js types) — its syntax errors gate Done (v1 hidden
     * required checkbox over `errors.length` → `modalForm.$invalid` → ftModal inactive). */
    private readonly codeField = viewChild(FtCodeFieldComponent);

    /** Snapshot of the edited field values, for rollback-on-cancel (v1 ft-snapshot). */
    private snapshot: Record<string, any> | null = null;
    private previousConfig: FieldEditorModalConfig | null = null;

    constructor() {
        effect(() => {
            const config = this.config();
            if (config && !this.previousConfig) {
                this.takeSnapshot(config);
            }
            this.previousConfig = config;
        });
    }

    /** Resolve a possibly-getter datasource to the live object (see the config doc). */
    protected resolveDatasource(c: FieldEditorModalConfig): any {
        return typeof c.datasource === 'function' ? c.datasource() : c.datasource;
    }

    /**
     * v1 modal_v1.html gated Done with `ng-disabled="modalForm.$invalid"` — the form aggregated
     * EVERY validator the FieldEditor factory rendered (fieldEditors.js availableOptionsByType):
     * required, ng-minlength/ng-maxlength, pattern, min/max, and the input `type` (url). Reproduce
     * that over the live datasource, with AngularJS semantics: non-required validators PASS on an
     * empty value, and ng-maxlength never blocks typing — it only invalidates. Evaluated per CD
     * like the v1 form watch, so it follows live edits and late-arriving datasources.
     */
    protected doneDisabled(c: FieldEditorModalConfig): boolean {
        // css/js code editors: any syntax-error diagnostic dims Save (v1 codeEditor validity).
        if ((this.codeField()?.syntaxErrorCount() ?? 0) > 0) return true;
        const o = c.fieldsOptions || {};
        const required = o['required'] != null && o['required'] !== false && o['required'] !== 'false';
        const minLength = o['ng-minlength'] != null ? +o['ng-minlength'] : null;
        const maxLength = o['ng-maxlength'] != null ? +o['ng-maxlength'] : null;
        const pattern = o['pattern'] ? safeRegExp(String(o['pattern'])) : null;
        const min = o['min'] != null ? +o['min'] : null;
        const max = o['max'] != null ? +o['max'] : null;
        const isUrl = o['type'] === 'url';
        if (!required && minLength == null && maxLength == null && !pattern && min == null && max == null && !isUrl) {
            return false;
        }
        const d = this.resolveDatasource(c);
        const list = Array.isArray(c.fields) ? c.fields : c.fields == null ? [] : [c.fields];
        const fields = list.map((f: any) => (typeof f === 'string' ? f : f?.name)).filter(Boolean);
        // Multi-language rows use the v1 `fieldData = language.data ? language.data : language` shape.
        const rows = Array.isArray(d) ? d.map((l: any) => (l && l.data ? l.data : l)) : [d];
        return rows.some((row: any) => fields.some((f: string) => {
            const v = row?.[f];
            if (v == null || v === '') return required;
            const s = String(v);
            if (minLength != null && s.length < minLength) return true;
            if (maxLength != null && s.length > maxLength) return true;
            if (pattern && !pattern.test(s)) return true;
            if (isUrl && !URL_REGEXP.test(s)) return true;
            if (min != null || max != null) {
                const n = parseFloat(s);
                if (Number.isNaN(n)) return true;
                if (min != null && n < min) return true;
                if (max != null && n > max) return true;
            }
            return false;
        }));
    }

    private takeSnapshot(c: FieldEditorModalConfig): void {
        this.snapshot = {};
        const list = Array.isArray(c.fields) ? c.fields : c.fields == null ? [] : [c.fields];
        const datasource = this.resolveDatasource(c);
        for (const f of list) {
            const name = typeof f === 'string' ? f : f?.name;
            if (name) this.snapshot[name] = datasource?.[name];
        }
    }

    protected onDone(): void {
        const onClose = this.config()?.onClose;
        const result = this.config()?.onDone?.();
        if (result && typeof result.then === 'function') {
            result.then(
                () => { onClose?.(); this.close(); },
                () => { onClose?.(); this.close(); },
            );
        } else {
            onClose?.();
            this.close();
        }
    }

    protected onCancel(): void {
        const c = this.config();
        if (c?.onCancel) {
            // Diff-save path: the host's onCancel restores its open-time snapshot itself.
            c.onCancel();
        } else if (c && this.snapshot) {
            // Bool path (unchanged): the shell's own per-field snapshot rollback.
            const datasource = this.resolveDatasource(c);
            for (const k of Object.keys(this.snapshot)) datasource[k] = this.snapshot[k];
        }
        c?.onClose?.();
        this.close();
    }

    private close(): void {
        this.snapshot = null;
        this.closed.emit();
    }
}

/** Verbatim AngularJS input.js URL_REGEXP — v1's `<input type="url">` ngModel validator. */
const URL_REGEXP =
    /^[a-z][a-z\d.+-]*:\/*(?:[^:@]+(?::[^@]+)?@)?(?:[^\s:/?#]+|\[[a-f\d:]+])(?::\d+)?(?:\/[^?#]*)?(?:\?[^#]*)?(?:#.*)?$/i;

/** `ng-pattern` anchored-regex semantics (AngularJS wraps the pattern in ^...$). */
function safeRegExp(pattern: string): RegExp | null {
    try {
        return new RegExp('^(?:' + pattern + ')$');
    } catch {
        return null;
    }
}
